/**
 * JSON Path Navigation for TipTap/ProseMirror
 *
 * Converts TipTap JSON paths to ProseMirror document positions.
 * This is the frontend equivalent of the backend's `map_offset_to_path()` function,
 * but works in reverse: path → position instead of offset → path.
 *
 * Example path: [0, 2, 1] means doc.content[0].content[2].content[1]
 */

import type { Node as ProseMirrorNode } from "prosemirror-model";

export interface JsonPath {
  path: number[]; // Indices from root to target node
  charOffset: number; // Character offset within the target text node
}

export class PathNavigationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PathNavigationError";
  }
}

/**
 * Maximum allowed depth for JSON paths to prevent DOS attacks
 * with deeply nested structures. TipTap documents rarely exceed
 * depth 5 (doc -> blockquote -> list -> listItem -> paragraph -> text)
 */
const MAX_PATH_DEPTH = 20;

/**
 * Navigate to a position in a ProseMirror document using a JSON path.
 *
 * @param doc - The ProseMirror document root node
 * @param path - Array of indices to navigate (e.g., [0, 2] = content[0].content[2])
 * @param charOffset - Character offset within the target text node
 * @returns The absolute ProseMirror position
 * @throws PathNavigationError if the path is invalid
 *
 * Algorithm:
 * 1. Navigate through the document following the path indices
 * 2. Track cumulative position as we traverse
 * 3. At each container node, add 1 for the opening token
 * 4. When we reach the target node, add the character offset
 * 5. Return the final position
 */
export function navigateToPath(
  doc: ProseMirrorNode,
  path: number[],
  charOffset: number
): number {
  if (path.length === 0) {
    throw new PathNavigationError("Path cannot be empty");
  }

  if (path.length > MAX_PATH_DEPTH) {
    throw new PathNavigationError(
      `Path depth ${path.length} exceeds maximum allowed depth ${MAX_PATH_DEPTH}. ` +
        `This may indicate malformed data or a potential DOS attack.`
    );
  }

  if (charOffset < 0) {
    throw new PathNavigationError(
      `Character offset must be non-negative, got: ${charOffset}`
    );
  }

  if (!Number.isInteger(charOffset)) {
    throw new PathNavigationError(
      `Character offset must be an integer, got: ${charOffset}`
    );
  }

  let currentNode: ProseMirrorNode = doc;
  let position = 0; // Start at position 0 (before doc opening token)

  // Navigate through the path
  for (let depth = 0; depth < path.length; depth++) {
    const index = path[depth];

    // Validate index is a non-negative integer
    if (!Number.isInteger(index)) {
      throw new PathNavigationError(
        `Path index at depth ${depth} must be an integer, got: ${index} (type: ${typeof index})`
      );
    }

    if (index < 0) {
      throw new PathNavigationError(
        `Path index at depth ${depth} must be non-negative, got: ${index}`
      );
    }

    // Validate that current node is a container with content
    if (!currentNode.content) {
      throw new PathNavigationError(
        `Node at path [${path.slice(0, depth).join(", ")}] has no content`
      );
    }

    // Validate index is within bounds
    if (index < 0 || index >= currentNode.content.childCount) {
      throw new PathNavigationError(
        `Index ${index} out of bounds at path [${path.slice(0, depth).join(", ")}] ` +
          `(node has ${currentNode.content.childCount} children)`
      );
    }

    // Add positions of all preceding siblings
    for (let i = 0; i < index; i++) {
      const sibling = currentNode.content.child(i);
      position += sibling.nodeSize;
    }

    // Move to the target child
    currentNode = currentNode.content.child(index);

    // If the target node is not a text node, add 1 for its opening token
    if (!currentNode.isText) {
      position += 1;
    }
  }

  // We've reached the target node - now add the character offset
  // Position now points to:
  // - For text nodes: the start of the text content
  // - For container nodes: inside the node (after opening token)

  if (currentNode.isText) {
    // Validate character offset doesn't exceed text length
    const textLength = currentNode.text?.length ?? 0;
    if (charOffset > textLength) {
      throw new PathNavigationError(
        `Character offset ${charOffset} exceeds text length ${textLength} at path [${path.join(", ")}]`
      );
    }

    // Text nodes: position is at start of text, just add character offset
    // ProseMirror absolute positions are 1-based within the document; after stepping into
    // the containing nodes, `position` points at the start of the text node content.
    // Therefore the correct absolute position for charOffset 0 is `position`.
    return position + charOffset;
  } else {
    // Container nodes: position is after opening token, add character offset
    // (though charOffset should typically be 0 for container nodes)
    return position + charOffset;
  }
}

/**
 * Navigate to a span defined by start and end paths.
 *
 * @param doc - The ProseMirror document root node
 * @param startPath - Path to the start node
 * @param startChar - Character offset within start node
 * @param endPath - Path to the end node
 * @param endChar - Character offset within end node
 * @returns Object with `from` and `to` ProseMirror positions
 * @throws PathNavigationError if either path is invalid
 */
export function navigateToSpan(
  doc: ProseMirrorNode,
  startPath: number[],
  startChar: number,
  endPath: number[],
  endChar: number
): { from: number; to: number } {
  const from = navigateToPath(doc, startPath, startChar);
  const to = navigateToPath(doc, endPath, endChar);

  if (to <= from) {
    throw new PathNavigationError(
      `End position (${to}) must be greater than start position (${from})`
    );
  }

  return { from, to };
}
